import { LeftOutlined, PlusOutlined } from "@ant-design/icons"
import { App, Button, Flex, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import {
  SCROLL_PAGE_SIZE,
  nextPageUrl
} from "../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../store.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import {
  copyProjectType,
  createProjectType,
  deleteProjectType,
  fetchProjectTypes
} from "../redux/templates-project-types.thunks.ts"
import { setTemplatesProp } from "../redux/templates.slice.ts"
import {
  type ProjectTypesFilters,
  SaveResultType
} from "../redux/templates.types.ts"
import type { ProjectTypeListParams } from "../services/services.types.ts"
import { type ProjectTypeTemplate } from "../types/project-type.types.ts"
import { AddProjectTypeFormModal } from "./components/add-project-type-form/AddProjectTypeFormModal.tsx"
import { ProjectTypesTemplates } from "./components/project-types-list-table/ProjectTypesTemplates.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"

const { Text, Title } = Typography

const ProjectTypesTemplatesPage = ({
  filters,
  saving,
  fetching,
  result,
  createProjectType,
  fetchProjectTypes,
  saveResult,
  permissions,
  setTemplatesProp,
  deleteProjectType,
  copyProjectType
}: ConnectedProps<typeof connector>) => {
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [showAdd, setShowAdd] = useState<boolean>(false)
  const [page, setPage] = useState<number>(1)
  const [ordering, setOrdering] = useState<string | null>(null)

  const handleGetProjectTypes = () => {
    const params = {
      page,
      page_size: SCROLL_PAGE_SIZE,
      ordering,
      ...filters
    }

    fetchProjectTypes(params)
  }

  const handleGetMoreProjectTypes = useCallback(() => {
    const url = nextPageUrl(result)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    fetchProjectTypes({
      ...extractParamsFromNextUrl<ProjectTypeListParams>(url),
      isMore: true
    })
  }, [result?.next, result?.retryNext, fetchProjectTypes])

  const handleChangeFilter = useCallback(
    (newData: Partial<ProjectTypesFilters>) => {
      setTemplatesProp({ key: "projectTypes.filters", value: newData })
      setPage(1)
    },
    [setTemplatesProp]
  )

  const handleChangeSearch = useCallback((value: string | null) => {
    handleChangeFilter({ q: value })
  }, [])

  useEffect(() => {
    handleGetProjectTypes()
  }, [page, ordering, filters])

  useEffect(() => {
    if (saveResult && saveResult.type === SaveResultType.PROJECT_TYPE_CREATE) {
      setShowAdd(false)
    }
  }, [saveResult])

  if (result?.results?.length === 0 && fetching) {
    return <Skeleton.Node className={"size-full"} />
  }

  const toggleAddModal = () => setShowAdd(!showAdd)

  const handleDeleteProjectType = (id: string) => {
    modal.confirm({
      centered: true,
      title: t("Confirm deleting"),
      content: t(
        "Are you sure you want to delete this project type? This action can NOT be undone."
      ),

      cancelText: t("Cancel"),
      okText: t("Delete"),
      okButtonProps: {
        danger: true,
        ghost: true
      },
      onOk() {
        deleteProjectType({ id, navigate })
      }
    })
  }

  const handleCopyProjectType = async (record: ProjectTypeTemplate) => {
    let customName = ""

    modal.confirm({
      centered: true,
      title: "Copy template",
      content: (
        <TextInput
          placeholder={"Optional custom name"}
          onChange={(e) => {
            customName = e.target.value
          }}
        />
      ),
      cancelText: "Cancel",
      okText: "Copy",
      async onOk() {
        const name = customName.trim()
        await copyProjectType({
          id: record?.id,
          ...(name ? { name } : {})
        }).unwrap()
        handleGetProjectTypes()
      }
    })
  }

  const handleSelectedRow = (record: ProjectTypeTemplate, action: string) => {
    switch (action) {
      case "view":
      case "edit":
        return navigate(RoutesPaths.settings.projectTemplate(record?.id))
      case "delete":
        return handleDeleteProjectType(record?.id)
      case "copy":
        return handleCopyProjectType(record)
    }
  }

  return (
    <div className={"grid w-full"}>
      <Flex vertical gap={"small"} className={"overflow-auto"}>
        <Flex align={"flex-start"} justify={"space-between"}>
          <Flex vertical>
            <Space>
              <Link to={RoutesPaths.settings.templates}>
                <Button icon={<LeftOutlined />} size={"small"} />
              </Link>
              <Title className={"m-0 text-start"} level={2}>
                {t("settingsTemplatesHub.cards.project.title")}
              </Title>
            </Space>
            <Text type={"secondary"}>
              {t("settingsTemplatesHub.cards.project.description")}
            </Text>
          </Flex>
          {permissions?.settings_template_create && (
            <Button
              onClick={toggleAddModal}
              type={"primary"}
              icon={<PlusOutlined />}
            >
              {t("settingsProjectTemplates.create")}
            </Button>
          )}
        </Flex>
        {showAdd && (
          <AddProjectTypeFormModal
            onClose={toggleAddModal}
            onSubmit={createProjectType}
            saving={saving}
          />
        )}

        <ProjectTypesTemplates
          loading={fetching}
          templates={result?.results || []}
          count={result?.count || 0}
          next={result?.next}
          retryNext={result?.retryNext}
          filters={filters}
          //
          deleteAllowed={permissions?.settings_template_delete}
          editAllowed={permissions?.settings_template_edit}
          copyAllowed={permissions?.settings_template_create}
          //
          onLoadMore={handleGetMoreProjectTypes}
          onOrderingChange={setOrdering}
          onSearchChange={handleChangeSearch}
          onSelectRow={handleSelectedRow}
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  filters: state.templates.projectTypes.filters,
  permissions: state.auth.permissions?.codes?.settings?.project_templates,
  saveResult: state.templates.saveResult,
  fetching: state.templates.fetching,
  saving: state.templates.saveFetching,
  result: state.templates.projectTypes.result
})

const mapDispatchToProps = {
  setTemplatesProp,
  createProjectType,
  copyProjectType,
  deleteProjectType,
  fetchProjectTypes
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectTypesTemplatesPage)
